import { getRecoilStateValue } from "../../Orchestrator/Recoils/Events/Recoil";
import { IPassengerSelection } from "../../Orchestrator/Types/Index";

// Single source of truth for resolving a passenger-type's display label,
// per product spec:
//   - For en-UK and fr-FR, the Transcity backend returns localized labels
//     via the `Accept-Language` header (sent by ApiRequest). We prefer
//     those API-provided labels over the local i18n translations so the
//     copy doesn't drift if marketing updates the backend.
//   - For ar-AE, Transcity does not yet ship Arabic labels. We fall back
//     to the local i18n translation (`adultLabel`, `child`, etc.) for
//     this case until Transcity catches up.
//   - If the API hasn't yet hydrated (`passengerTypesStateAtom` is empty
//     on first render) OR doesn't carry a label for this internal type,
//     we also fall back to the local i18n key.
//
// The `passengerId` argument is the search-state internal key
// (`"adults"`, `"children"`, `"infants"`, `"seniorcitizens"`, `"students"`,
// `"reducedMobility"`, `"wheelchair"`) — matches `IPassengerSelection.id`
// produced by `GetPassengerTypesAction`. The `fallbackI18nKey` is the
// local-translation key to use for Arabic / hydration-gap fallback.
export const resolveSingularPassengerLabel = (
    passengerId: string,
    fallbackI18nKey: string,
    intl: any,
): string => {
    const formatFromI18n = (): string =>
        intl?.formatMessage?.({ id: fallbackI18nKey }) ?? "";

    // Read the API-provided list up front so the AR-exclusion clause
    // below stays a single line that's trivial to delete the day
    // Transcity ships Arabic labels.
    const types = getRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom");
    const apiType = Array.isArray(types)
        ? types.find((t) => t.id === passengerId)
        : undefined;

    // AR-EXCLUSION: Transcity doesn't yet ship Arabic labels. Until it
    // does, force AR through the local-i18n fallback even when the API
    // happens to carry a label. Delete this `&& intl?.locale !== "ar"`
    // clause when AR is supported on the backend.
    if (apiType?.label && intl?.locale !== "ar") {
        return apiType.label;
    }

    // Fall back to local i18n, then the i18n key itself, then the
    // internal `passengerId` — guarantees a non-empty string even when
    // `intl` is undefined and the API hasn't hydrated (rare path:
    // non-React unit-test callers or pre-hydration first renders).
    return formatFromI18n() || fallbackI18nKey || passengerId;
};

// Maps internal passenger-type IDs (the keys used in search state and
// `IPassengerSelection.id`) to the local i18n message keys carrying the
// singular form of the label. Shared across the four formatter copies so
// they stay in lockstep.
export const PASSENGER_ID_TO_I18N_KEY: Readonly<Record<string, string>> = {
    adults: "adultLabel",
    children: "child",
    infants: "infant",
    seniorcitizens: "senior_citizens",
    students: "studentLabel",
    reducedMobility: "personReducedMobility",
    wheelchair: "personWheelchair",
};

// Maps the English label form (as it appears in legacy receipt
// descriptions built by `addPassengerDetail`) to the internal passenger
// type ID. Used by `getParsePassengerLabel` to bridge the English-only
// receipt-description input back into the API-driven resolver.
export const PASSENGER_ENGLISH_LABEL_TO_ID: Readonly<Record<string, string>> = {
    "Adult": "adults",
    "Children": "children",
    "Infant": "infants",
    "Senior Citizen (UAE)": "seniorcitizens",
    "Student": "students",
    "People with Reduced Mobility": "reducedMobility",
    "Wheelchair": "wheelchair",
};
